/**
 * Student-only feedback form, shown after they leave the classroom.
 */
import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";

const STARS = [1, 2, 3, 4, 5];

export default function ClassroomFeedback() {
  const { lessonId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const session = location.state;
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!session || session.lesson_id !== lessonId || session.role !== "student") {
    return (
      <main className="min-h-[100dvh] bg-[#FAF9F6] pt-24 px-6" data-testid="feedback-page">
        <div className="max-w-md mx-auto text-[#1C1C1C]">
          <p>This feedback link is no longer valid.</p>
        </div>
      </main>
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (rating < 1) {
      setError("Please select a rating.");
      return;
    }
    setLoading(true);
    try {
      await api.feedback(session.passcode, rating, comments);
      setDone(true);
    } catch (err) {
      setError(err.message || "Could not submit feedback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6"
      data-testid="feedback-page"
    >
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          Student feedback
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          {done ? "Gracias." : "How was your lesson?"}
        </h1>

        {done ? (
          <div className="mt-6 space-y-4">
            <p className="text-[#1C1C1C]/70">
              Your feedback helps us improve. The recording of your lesson will
              be available shortly.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
            >
              Back to home
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 space-y-6">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
                Rating
              </div>
              <div className="flex gap-2" data-testid="feedback-stars">
                {STARS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`feedback-star-${n}`}
                    onClick={() => setRating(n)}
                    className={`w-10 h-10 rounded-full border transition-colors ${
                      n <= rating
                        ? "bg-[#1C1C1C] text-[#FAF9F6] border-[#1C1C1C]"
                        : "bg-white text-[#1C1C1C]/40 border-[#1C1C1C]/15 hover:border-[#1C1C1C]/40"
                    }`}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
                Comments (optional)
              </label>
              <textarea
                data-testid="feedback-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={5}
                className="w-full px-4 py-3 bg-white border border-[#1C1C1C]/10 rounded-xl text-base focus:outline-none focus:border-[#1C1C1C]/40 transition-colors resize-y"
                placeholder="What worked well? What could improve?"
              />
            </div>

            {error && (
              <div className="text-sm text-[#B83A3A]" data-testid="feedback-error">
                {error}
              </div>
            )}

            <button
              data-testid="feedback-submit"
              type="submit"
              disabled={loading}
              className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
            >
              {loading ? "Sending…" : "Submit feedback"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
